// A text input with a suggestion list under it, for the site search and
// the person and event pickers. Plain DOM, so pages need no React.
//
// options:
//   url(query)   JSON endpoint returning [{id, name, ...}]
//   href(item)   suggestions are links to this URL, or
//   valueOf(item) picking a suggestion puts this value in the input
//   placeholder, name, id  set on the input, if the container has none
let count = 0;

// The server's portrait placeholder (ApplicationHelper::PORTRAIT_PLACEHOLDER_SVG),
// colored by the .portrait_ph rules.
const PORTRAIT_PLACEHOLDER =
  '<svg class="portrait_ph" viewBox="0 0 1 1" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">' +
  '<rect width="1" height="1"/><circle cx="0.5" cy="0.36" r="0.19"/>' +
  '<path d="M0.12,1 C0.12,0.7 0.28,0.58 0.5,0.58 C0.72,0.58 0.88,0.7 0.88,1 Z"/></svg>';

// A square slot for a result's portrait (people) or icon (other records).
// It is there even when the result has neither, so every name lines up: a
// person gets the silhouette, anything else an empty square.
const thumbnail = (item) => {
  const slot = document.createElement("span");
  const isPerson = item.class === "Person";
  slot.className = isPerson ? "suggest_thumb" : "suggest_thumb suggest_icon";
  if (isPerson) slot.innerHTML = PORTRAIT_PLACEHOLDER;
  if (item.image_url) {
    const img = document.createElement("img");
    img.src = item.image_url;
    img.alt = "";
    img.loading = "lazy";
    img.addEventListener("error", () => img.remove());
    slot.append(img);
  }
  return slot;
};

export default function autocomplete(container, { url, href, valueOf, placeholder = "Search", name, id }) {
  const listId = `autocomplete_${++count}`;
  // Search boxes come with their input in the HTML, so the page doesn't
  // shift when this script runs; the pickers get theirs made here.
  let input = container.querySelector("input");
  if (!input) {
    input = document.createElement("input");
    input.type = "text";
    input.placeholder = placeholder;
    input.setAttribute("aria-label", placeholder);
    if (name) input.name = name;
    if (id) input.id = id;
  }
  input.autocomplete = "off";
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-expanded", "false");
  input.setAttribute("aria-controls", listId);

  const list = document.createElement("ul");
  list.id = listId;
  list.className = "autocomplete_list";
  list.setAttribute("role", "listbox");
  list.hidden = true;

  container.append(input, list);

  let items = [];
  let active = -1;
  let latest = 0;
  let timer;

  const close = () => {
    list.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    active = -1;
  };

  const pick = (i) => {
    const item = items[i];
    if (!item) return;
    if (href) {
      window.location.href = href(item);
    } else {
      input.value = valueOf(item);
      close();
    }
  };

  const highlight = (i) => {
    const options = list.children;
    if (active >= 0 && options[active]) options[active].removeAttribute("aria-selected");
    active = i;
    if (i >= 0 && options[i]) {
      options[i].setAttribute("aria-selected", "true");
      input.setAttribute("aria-activedescendant", options[i].id);
      options[i].scrollIntoView({ block: "nearest" });
    }
  };

  const render = () => {
    list.replaceChildren(
      ...items.map((item, i) => {
        const li = document.createElement("li");
        li.id = `${listId}_${i}`;
        li.setAttribute("role", "option");
        const label = document.createElement(href ? "a" : "span");
        label.className = "suggest_item";
        // Site search results carry image_url; the pickers' results don't.
        if ("image_url" in item) label.append(thumbnail(item));
        const text = document.createElement("span");
        text.textContent = item.name;
        label.append(text);
        if (href) label.href = href(item);
        li.append(label);
        // Keep focus in the input so blur doesn't close the list mid-press;
        // a link still navigates on click.
        li.addEventListener("mousedown", (e) => {
          e.preventDefault();
          if (!href) pick(i);
        });
        return li;
      })
    );
    active = -1;
    list.hidden = items.length === 0;
    input.setAttribute("aria-expanded", String(items.length > 0));
  };

  const fetchSuggestions = async (query) => {
    const mine = ++latest;
    try {
      const response = await fetch(url(encodeURIComponent(query)));
      const data = await response.json();
      if (mine !== latest) return; // a newer query is on its way
      items = data;
      render();
    } catch {
      // Leave the list as it was; the form still submits to the search page.
    }
  };

  input.addEventListener("input", () => {
    clearTimeout(timer);
    const query = input.value.trim();
    if (!query) {
      latest++;
      items = [];
      render();
      return;
    }
    timer = setTimeout(() => fetchSuggestions(query), 150);
  });

  input.addEventListener("keydown", (e) => {
    if (list.hidden) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      highlight(Math.min(active + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      highlight(Math.max(active - 1, -1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(active);
    } else if (e.key === "Escape") {
      close();
    }
  });

  input.addEventListener("blur", () => setTimeout(close, 150));
  input.addEventListener("focus", () => {
    if (items.length) render();
  });
}
